import { apiService, type Request } from '../services/apiService';
import type { AnalyzeRequestInput } from './analyzeRequest';
import { parsePathAndQueryString } from './queryParamsExecutionUtils';

const STORAGE_KEY_COLLECTIONS_NAV = 'collectionsNavState';

type HeaderRow = { key?: string; value?: string; description?: string; enabled?: boolean };

function getPathOnly(pathField: string): string {
  const s = (pathField ?? '').trim();
  const qIndex = s.indexOf('?');
  if (qIndex === -1) {
    return s;
  }
  return s.slice(0, qIndex);
}

function parsePathAndQuery(pathOrUrl: string): { path: string; params: HeaderRow[] } {
  const { path, params } = parsePathAndQueryString(pathOrUrl);
  return {
    path,
    params: params.map((p) => ({ key: p.key, value: p.value, description: '', enabled: true })),
  };
}

function parseHeadersFromRequest(request: Request): HeaderRow[] {
  if (!request.headers) {
    return [];
  }
  try {
    const parsedHeaders = JSON.parse(request.headers);
    if (Array.isArray(parsedHeaders)) {
      return parsedHeaders.map((h: HeaderRow) => ({
        key: h.key || '',
        value: h.value ?? '',
        description: h.description || '',
        enabled: h.enabled !== false,
      }));
    }
    if (typeof parsedHeaders === 'object' && parsedHeaders !== null) {
      return Object.entries(parsedHeaders).map(([key, value]) => ({
        key,
        value: String(value || ''),
        description: '',
        enabled: true,
      }));
    }
  } catch {
    // ignore parse errors
  }
  return [];
}

function parseQueryParamsFromRequest(request: Request): HeaderRow[] {
  if (request.url?.includes('?')) {
    return parsePathAndQuery(request.url).params;
  }
  if (request.description) {
    try {
      const params = JSON.parse(request.description);
      if (Array.isArray(params)) {
        return params.map((p: { key?: string; value?: string; enabled?: boolean }) => ({
          key: p.key != null ? String(p.key) : '',
          value: p.value != null ? String(p.value) : '',
          description: '',
          enabled: p.enabled !== false,
        }));
      }
      if (params && typeof params === 'object') {
        return Object.entries(params).map(([key, value]) => ({
          key,
          value: String(value ?? ''),
          description: '',
          enabled: true,
        }));
      }
    } catch {
      // ignore parse errors
    }
  }
  return [];
}

function resolveBodyFromRequest(request: Request): string {
  if (request.method === 'GET' || request.method === 'HEAD') {
    return '';
  }
  if (request.body && typeof request.body === 'string' && request.body.trim()) {
    return request.body;
  }
  if (request.body && typeof request.body === 'object' && request.body !== null) {
    try {
      return JSON.stringify(request.body, null, 2);
    } catch {
      return '{}';
    }
  }
  return '';
}

export interface RequestAnalysisContext extends AnalyzeRequestInput {
  requestId: number;
  collectionId: number | null;
}

export function readCollectionsNavFromStorage(): { collectionId?: number; requestId?: number; projectId?: number } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_COLLECTIONS_NAV);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return {
      collectionId: parsed.collectionId != null ? Number(parsed.collectionId) : undefined,
      requestId: parsed.requestId != null ? Number(parsed.requestId) : undefined,
      projectId: parsed.projectId != null ? Number(parsed.projectId) : undefined,
    };
  } catch {
    return {};
  }
}

export async function loadRequestAnalysisContext(
  requestId: number,
  projectId?: number
): Promise<RequestAnalysisContext> {
  const request = await apiService.getRequest(requestId, projectId);

  let rawBody = resolveBodyFromRequest(request);
  if (!rawBody) {
    try {
      const dataSets = await apiService.getDataSetsByRequest(requestId);
      const firstBody = dataSets.find((ds) => ds.body && String(ds.body).trim())?.body;
      if (firstBody) {
        rawBody = typeof firstBody === 'string' ? firstBody : JSON.stringify(firstBody, null, 2);
      }
    } catch {
      // ignore data set load errors
    }
  }

  const queryParams = parseQueryParamsFromRequest(request);
  const headers = parseHeadersFromRequest(request);
  const path = request.url ?? '';

  return {
    requestId: request.id,
    collectionId: request.collectionId ?? null,
    requestName: request.name ?? '',
    hasCollection: request.collectionId != null,
    hasEnvironment: Boolean(request.environmentId || request.environmentUrl || request.environment),
    path,
    queryParams,
    headers,
    rawBody,
    bodyType: request.bodyType || 'none',
  };
}

export async function persistAnalyzeRequestUpdates(
  requestId: number,
  current: Pick<RequestAnalysisContext, 'path' | 'rawBody'>,
  updates: { path?: string; body?: string },
  projectId?: number
): Promise<void> {
  const payload: Partial<Request> = {};
  if (updates.path && updates.path !== current.path) {
    payload.url = getPathOnly(updates.path);
  }
  if (updates.body && updates.body !== current.rawBody) {
    payload.body = updates.body;
  }
  if (Object.keys(payload).length === 0) {
    return;
  }
  await apiService.updateRequest(requestId, payload);
}
